interface SEOHeadProps {
  title: string;
  description: string;
  canonicalUrl: string;
  ogType?: string;
  structuredData?: Record<string, unknown>;
}

export default function SEOHead({ title, description, canonicalUrl, ogType = 'website', structuredData }: SEOHeadProps) {
  const pageTitle = `${title} | 华体会官网`;

  const updateMetaTags = () => {
    document.title = pageTitle;

    const setMeta = (name: string, content: string, property?: boolean) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('og:title', pageTitle, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('twitter:title', pageTitle);
    setMeta('twitter:description', description);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);
  };

  const injectStructuredData = () => {
    if (!structuredData) return;
    const existingScript = document.getElementById('json-ld-structured-data');
    if (existingScript) {
      existingScript.remove();
    }
    const script = document.createElement('script');
    script.id = 'json-ld-structured-data';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData, null, 2);
    document.head.appendChild(script);
  };

  const cleanup = () => {
    const script = document.getElementById('json-ld-structured-data');
    if (script) script.remove();
  };

  if (typeof window !== 'undefined') {
    updateMetaTags();
    injectStructuredData();
  }

  return {
    cleanup,
  };
}

export function getOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '华体会官网',
    url: 'https://www.huatiyuiguanwang.com',
    description: '华体会官网是您体验顶级体育娱乐的首选平台，提供安全稳定的官方正版入口，汇聚海量体育赛事、实时赔率与专业竞猜服务。',
    logo: 'https://www.huatiyuiguanwang.com/logo.png',
    sameAs: [
      'https://www.huatiyuiguanwang.com',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-000-0000',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };
}

export function getWebsiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '华体会官网',
    url: 'https://www.huatiyuiguanwang.com',
    description: '华体会官网是您体验顶级体育娱乐的首选平台，提供安全稳定的官方正版入口。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.huatiyuiguanwang.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };
}

export function getBreadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function getArticleSchema(article: {
  title: string;
  description: string;
  url: string;
  datePublished: string;
  author: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    url: article.url,
    datePublished: article.datePublished,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '华体会官网',
    },
  };
}

export function getFAQSchema(faqs: { question: string; answer: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

export function getWebPageSchema(title: string, description: string, url: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description: description,
    url: url,
    isPartOf: {
      '@type': 'WebSite',
      name: '华体会官网',
      url: 'https://www.huatiyuiguanwang.com',
    },
  };
}